import React, { useEffect, useRef, useState } from "react";
import { IrisLeadLink, toastApiError, useMountedRef } from "./common";
import "./Import.css";

// Read-only live check for an add-contacts ("red") list. The server reads every
// lead from Iris in parallel and runs the same dedupe the real run uses
// (services/contact_append.run_live_check); this panel only starts it, polls
// GET /import/contacts/live-check/{upload_id} and draws progress + running
// totals. Nothing is written to Iris here.

const POLL_MS = 2000;

const fmt = (n) => Number(n || 0).toLocaleString();

function durationText(seconds) {
	if (seconds === undefined || seconds === null) return "";
	if (seconds < 60) return "under a minute";
	const min = Math.round(seconds / 60);
	return `about ${min} min`;
}

// Numbers glide between polls instead of jumping (skipped for reduced motion).
function useAnimatedNumber(target) {
	const [shown, setShown] = useState(target || 0);
	const fromRef = useRef(target || 0);
	useEffect(() => {
		const to = Number(target || 0);
		const reduce =
			typeof window !== "undefined" &&
			window.matchMedia &&
			window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		if (reduce) {
			fromRef.current = to;
			setShown(to);
			return undefined;
		}
		const from = fromRef.current;
		const start = performance.now();
		const ms = 900;
		let frame;
		const step = (now) => {
			const t = Math.min(1, (now - start) / ms);
			const eased = 1 - Math.pow(1 - t, 3);
			const value = Math.round(from + (to - from) * eased);
			setShown(value);
			fromRef.current = value;
			if (t < 1) frame = requestAnimationFrame(step);
		};
		frame = requestAnimationFrame(step);
		return () => cancelAnimationFrame(frame);
	}, [target]);
	return shown;
}

function StatTile({ tone, value, label, sub }) {
	const shown = useAnimatedNumber(value);
	return (
		<div className={`liveStat ${tone}`}>
			<div className="liveStatValue">{fmt(shown)}</div>
			<div className="liveStatLabel">{label}</div>
			{sub && <div className="liveStatSub">{sub}</div>}
		</div>
	);
}

// The check needs a minute or two of Iris reads per ~250 leads; reuse a fresh
// one (same file, < 24h) instead of re-reading everything.
function needsNewCheck(state) {
	if (!state || state.status === "none" || state.status === "failed") return true;
	if (state.status === "done" && !state.fresh) return true;
	return false;
}

export default function LiveCheckPanel({ api, uploadId, onResult }) {
	const mountedRef = useMountedRef();
	const [state, setState] = useState(null);
	const [error, setError] = useState(null);
	const [restartKey, setRestartKey] = useState(0);

	useEffect(() => {
		if (!uploadId) return undefined;
		let cancelled = false;
		let timer = null;

		const poll = async () => {
			try {
				const s = await api.getLiveCheck(uploadId);
				if (cancelled) return;
				setState(s);
				setError(null);
				if (s.status === "running") timer = setTimeout(poll, POLL_MS);
			} catch (err) {
				if (cancelled) return;
				setError(err);
				if (err?.status !== 401 && err?.status !== 403) timer = setTimeout(poll, POLL_MS * 3);
			}
		};

		const begin = async () => {
			try {
				const existing = await api.getLiveCheck(uploadId);
				if (cancelled) return;
				if (restartKey === 0 && !needsNewCheck(existing)) {
					setState(existing);
					if (existing.status === "running") timer = setTimeout(poll, POLL_MS);
					return;
				}
				const started = await api.startLiveCheck(uploadId);
				if (cancelled) return;
				setState({ ...started, status: started.status || "running" });
				timer = setTimeout(poll, POLL_MS);
			} catch (err) {
				if (cancelled) return;
				setError(err);
				toastApiError(err, "Could not start the live check");
			}
		};

		setState(null);
		begin();
		return () => {
			cancelled = true;
			if (timer) clearTimeout(timer);
		};
	}, [api, uploadId, restartKey]);

	useEffect(() => {
		if (mountedRef.current && onResult) onResult(state);
	}, [state, onResult, mountedRef]);

	const status = state?.status;
	const running = status === "running" || (!state && !error);
	const done = status === "done";
	const failed = status === "failed" || (error && !state);
	const total = state?.total_leads || 0;
	const checked = state?.checked || 0;
	const pct = total ? Math.min(100, Math.round((checked / total) * 100)) : 0;
	const problems = state?.errors || [];

	let pill = { cls: "running", text: "Checking Iris" };
	if (done) pill = { cls: "done", text: "Checked" };
	if (failed) pill = { cls: "failed", text: "Check failed" };

	return (
		<section className="liveCheck" aria-live="polite">
			<header className="liveCheckHead">
				<div>
					<div className="liveCheckTitle">Live check in Iris</div>
					<div className="liveCheckSub">
						Every lead is read from Iris and checked exactly like the real run. Nothing is
						written.
					</div>
				</div>
				<span className={`livePill ${pill.cls}`}>
					<span className="liveDot" />
					{pill.text}
				</span>
			</header>

			<div className={`liveBar ${running ? "active" : ""} ${failed ? "failed" : ""}`}>
				<div className="liveBarFill" style={{ width: `${done ? 100 : pct}%` }} />
			</div>
			<div className="liveBarMeta">
				<span>
					{state ? (
						<>
							<b>{fmt(checked)}</b> of {fmt(total)} leads checked
						</>
					) : (
						"Starting…"
					)}
				</span>
				<span>
					{running && state?.eta_seconds !== undefined && `${durationText(state.eta_seconds)} left`}
					{done && state?.elapsed_seconds !== undefined && `took ${durationText(state.elapsed_seconds)}`}
					{running && state?.eta_seconds === undefined && state && `${pct}%`}
				</span>
			</div>

			<div className="liveStats">
				<StatTile
					tone="add"
					value={state?.would_add}
					label="new contacts to add"
					sub={`on ${fmt(state?.leads_getting_contacts)} leads`}
				/>
				<StatTile
					tone="skip"
					value={state?.already_on_lead}
					label="already on their lead"
					sub="will be skipped"
				/>
				<StatTile
					tone={state?.leads_with_errors ? "warn" : "quiet"}
					value={state?.leads_with_errors}
					label="leads with a problem"
					sub={state?.leads_with_errors ? "listed below" : "none so far"}
				/>
			</div>

			{failed && (
				<div className="resultBanner error">
					The live check stopped: {state?.error || error?.message || "unknown error"}.{" "}
					<button
						type="button"
						className="importBtn small secondary"
						onClick={() => setRestartKey((k) => k + 1)}>
						Try again
					</button>
				</div>
			)}

			{done && (state?.sample_adds || []).length > 0 && (
				<details className="liveDetails" open>
					<summary>Who gets added (first {state.sample_adds.length} leads)</summary>
					<div className="importTableWrap">
						<table className="importTable">
							<thead>
								<tr>
									<th>Lead</th>
									<th>New contact(s)</th>
								</tr>
							</thead>
							<tbody>
								{state.sample_adds.map((l) => (
									<tr key={l.lead_id}>
										<td>
											<IrisLeadLink leadId={l.lead_id}>{l.company}</IrisLeadLink>
										</td>
										<td>{l.add.join(", ")}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</details>
			)}

			{problems.length > 0 && (
				<details className="liveDetails" open={done}>
					<summary>{problems.length} lead(s) with a problem</summary>
					<div className="importTableWrap">
						<table className="importTable">
							<thead>
								<tr>
									<th>Lead</th>
									<th>Problem</th>
								</tr>
							</thead>
							<tbody>
								{problems.map((p) => (
									<tr key={p.lead_id}>
										<td>
											<IrisLeadLink leadId={p.lead_id}>{p.company}</IrisLeadLink>
										</td>
										<td>{p.error}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</details>
			)}

			{running && (
				<p className="importMuted liveFootnote">
					You can leave this page — the check keeps running and the result is kept for 24 hours.
				</p>
			)}
		</section>
	);
}
